import { useState, useEffect, useCallback } from 'react'
import apiService from '@/services/api'
import { FolderScope } from '@/types'

interface SmartOrganizeJobStatus {
  job_id: string
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
  progress: number
  total_files: number
  processed_files: number
  created_at: string
  completed_at?: string
}

interface UseSmartOrganizeStatusReturn {
  activeJob: SmartOrganizeJobStatus | null
  hasActiveJob: boolean
  hasCompletedJob: boolean
  loading: boolean
  error: string | null
  checkStatus: () => void
}

export function useSmartOrganizeStatus(folderId: FolderScope | null): UseSmartOrganizeStatusReturn {
  const [activeJob, setActiveJob] = useState<SmartOrganizeJobStatus | null>(null)
  const [hasCompletedJob, setHasCompletedJob] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const checkStatus = useCallback(async () => {
    if (!folderId) return

    setLoading(true)
    setError(null)

    try {
      const response = await apiService.getFolderSmartOrganizeJobs(folderId)

      const activeJobData = response.jobs.find(
        (job) => job.status === 'pending' || job.status === 'processing'
      )

      if (activeJobData) {
        setActiveJob({
          job_id: activeJobData.job_id,
          status: activeJobData.status as SmartOrganizeJobStatus['status'],
          progress: activeJobData.progress,
          total_files: activeJobData.total_files,
          processed_files: activeJobData.processed_files,
          created_at: activeJobData.created_at,
          completed_at: activeJobData.completed_at,
        })
      } else {
        setActiveJob(null)
      }

      const hasCompleted = response.jobs.some(
        (job) => job.status === 'completed' && job.has_results
      )
      setHasCompletedJob(hasCompleted)
    } catch (err) {
      console.error('Failed to check smart organize status:', err)
      setError('Failed to check status')
      setActiveJob(null)
      setHasCompletedJob(false)
    } finally {
      setLoading(false)
    }
  }, [folderId])

  useEffect(() => {
    checkStatus()
  }, [checkStatus])

  useEffect(() => {
    if (!activeJob) return

    const interval = setInterval(checkStatus, 3000)
    return () => clearInterval(interval)
  }, [activeJob, checkStatus])

  return {
    activeJob,
    hasActiveJob: !!activeJob,
    hasCompletedJob,
    loading,
    error,
    checkStatus,
  }
}
